/**
 * Debug visualization toggle states.
 */
export interface DebugState {
  showCollisionGrid: boolean;
  showPathMarkers: boolean;
  showGridLines: boolean;
  showDestinationMarkers: boolean;
  showLineOfSight: boolean;
  showTimeDisplay: boolean;
  showLighting: boolean;
  showAttackArcs: boolean;
  showHitboxes: boolean;
  showChunkBoundaries: boolean;
  showMouseCoordinates: boolean;
  showEdgeCoordinates: boolean;
}

export const DEFAULT_DEBUG_STATE: DebugState = {
  showCollisionGrid: false,
  showPathMarkers: false,
  showGridLines: false,
  showDestinationMarkers: false,
  showLineOfSight: false,
  showTimeDisplay: true,
  showLighting: true, // Lighting on by default
  showAttackArcs: true,
  showHitboxes: false,
  showChunkBoundaries: false,
  showMouseCoordinates: false,
  showEdgeCoordinates: false,
};

const DEBUG_STORAGE_KEY = 'kernow_debug_settings';

/**
 * Client-only service for debug visualization state.
 */
export class DebugService {
  /**
   * Current debug visualization state.
   */
  public state: DebugState;

  constructor() {
    this.state = this.loadFromStorage();
  }

  /**
   * Toggle collision grid visibility.
   */
  public toggleCollisionGrid(): boolean {
    this.state.showCollisionGrid = !this.state.showCollisionGrid;
    this.saveToStorage();
    return this.state.showCollisionGrid;
  }

  /**
   * Toggle path markers visibility.
   */
  public togglePathMarkers(): boolean {
    this.state.showPathMarkers = !this.state.showPathMarkers;
    this.saveToStorage();
    return this.state.showPathMarkers;
  }

  /**
   * Toggle grid lines visibility.
   */
  public toggleGridLines(): boolean {
    this.state.showGridLines = !this.state.showGridLines;
    this.saveToStorage();
    return this.state.showGridLines;
  }

  /**
   * Toggle destination markers visibility.
   */
  public toggleDestinationMarkers(): boolean {
    this.state.showDestinationMarkers = !this.state.showDestinationMarkers;
    this.saveToStorage();
    return this.state.showDestinationMarkers;
  }

  /**
   * Toggle line-of-sight visibility.
   */
  public toggleLineOfSight(): boolean {
    this.state.showLineOfSight = !this.state.showLineOfSight;
    this.saveToStorage();
    return this.state.showLineOfSight;
  }

  /**
   * Toggle time display visibility.
   */
  public toggleTimeDisplay(): boolean {
    this.state.showTimeDisplay = !this.state.showTimeDisplay;
    this.saveToStorage();
    return this.state.showTimeDisplay;
  }

  /**
   * Toggle lighting/darkness overlay visibility.
   */
  public toggleLighting(): boolean {
    this.state.showLighting = !this.state.showLighting;
    this.saveToStorage();
    return this.state.showLighting;
  }

  /**
   * Toggle attack arc visibility.
   */
  public toggleAttackArcs(): boolean {
    this.state.showAttackArcs = !this.state.showAttackArcs;
    this.saveToStorage();
    return this.state.showAttackArcs;
  }

  /**
   * Toggle hitbox visibility.
   */
  public toggleHitboxes(): boolean {
    this.state.showHitboxes = !this.state.showHitboxes;
    this.saveToStorage();
    return this.state.showHitboxes;
  }

  /**
   * Reset the service state to defaults.
   *
   * NOTE: We use Object.assign instead of re-assigning this.state to
   * preserve the Vue Proxy reference if it has been wrapped externally
   * (e.g. in main.ts). This ensures UI reactivity remains intact.
   */
  public reset(): void {
    Object.assign(this.state, DEFAULT_DEBUG_STATE);
    this.saveToStorage();
  }

  /**
   * Update a specific debug state option.
   */
  public updateState(key: keyof DebugState, value: boolean): void {
    this.state[key] = value;
    this.saveToStorage();
  }

  /**
   * Load debug state from localStorage.
   */
  private loadFromStorage(): DebugState {
    try {
      const stored = localStorage.getItem(DEBUG_STORAGE_KEY);
      if (!stored) {
        return { ...DEFAULT_DEBUG_STATE };
      }

      // Cast to Partial to allow for missing keys in older stored state
      const parsed = JSON.parse(stored) as Partial<DebugState>;

      // Merge with defaults to ensure any new keys added in future versions are present
      return { ...DEFAULT_DEBUG_STATE, ...parsed };
    } catch (e) {
      console.warn('Failed to parse debug state from localStorage', e);
      return { ...DEFAULT_DEBUG_STATE };
    }
  }

  /**
   * Save current debug state to localStorage.
   */
  private saveToStorage(): void {
    try {
      localStorage.setItem(DEBUG_STORAGE_KEY, JSON.stringify(this.state));
    } catch (e) {
      console.warn('Failed to save debug state to localStorage', e);
    }
  }
}
